// components/notifications/person-notifications.component.jsx
import { getCallActivity, subscribeCallActivity } from "../meet/callActivity";
import { registerBrowserToken } from "../../firebase/notificationSession";
import {
  migrateChatMutes,
  respondToCall,
  chatKey,
} from "../../firebase/chatPreferences";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  BsBellFill,
  BsFillTelephoneInboundFill,
  BsTelephoneXFill,
} from "react-icons/bs";

import {
  db,
  fieldValue,
  getToken,
  personNotfUpdateDeleteAll,
  personNotfUpdateSeen,
  personNotfUpdateSeenAll,
} from "../../firebase/firebase.utils";

import Avatar from "../retro/avatar/avatar.component";
import MobileBurger from "../mobile-drawer/mobileBurger";
import PersonNotification, {
  getNotificationCallType,
  getCallChatParams,
} from "./notification/person-notification.component";

import "./person-notifications.styles.scss";

const NotificationContext = createContext(null);

const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "NotificationBell must be rendered inside PersonNotifications",
    );
  }

  return context;
};

function getDeviceType() {
  if (typeof navigator === "undefined") return "desktop";

  const userAgent = navigator.userAgent;

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
    return "mobile";
  }

  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      userAgent,
    )
  ) {
    return "mobile";
  }

  return "desktop";
}

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;

  const iosPlatforms = [
    "iPad Simulator",
    "iPhone Simulator",
    "iPod Simulator",
    "iPad",
    "iPhone",
    "iPod",
  ];

  return (
    iosPlatforms.includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") &&
      typeof document !== "undefined" &&
      "ontouchend" in document)
  );
}

function playAudio(audioElement, restart = false) {
  if (!audioElement) return;

  if (restart) {
    audioElement.currentTime = 0;
  }

  const playPromise = audioElement.play();

  if (playPromise?.catch) {
    playPromise.catch(() => {});
  }
}

function stopAudio(audioElement) {
  if (!audioElement) return;

  audioElement.pause();
  audioElement.currentTime = 0;
}

export const NotificationBell = () => {
  const {
    notifications,
    unseenCount,
    showNotifications,
    setShowNotifications,
    showSnackBar,
    currentUserId,
    handleMarkSeenAll,
    handleDeleteAll,
    handleClose,
  } = useNotifications();

  const bellRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    if (!showNotifications || !bellRef.current) {
      setMenuPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = bellRef.current.getBoundingClientRect();

      setMenuPosition({
        top: rect.bottom + 10,
        right: window.innerWidth - rect.right,
      });
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [showNotifications]);

  return (
    <div className="notificationBell" ref={bellRef}>
      <motion.button
        type="button"
        className="notifications__bell-button"
        aria-label={
          showNotifications ? "Close notifications" : "Open notifications"
        }
        aria-expanded={showNotifications}
        onClick={() => setShowNotifications((currentValue) => !currentValue)}
        animate={
          showSnackBar
            ? {
                rotate: [0, 20, 0, -20, 0, 15, 0, -15, 0],
                transformOrigin: "top",
              }
            : { rotate: 0 }
        }
        transition={{
          duration: 2,
        }}
      >
        <BsBellFill size="1.2em" />
      </motion.button>

      {unseenCount > 0 && (
        <div className="notifications__badge" aria-hidden="true">
          <i>{unseenCount}</i>
        </div>
      )}

      {showNotifications &&
        menuPosition &&
        createPortal(
          <div
            className="person__notifications"
            style={{
              top: menuPosition.top,
              right: menuPosition.right,
            }}
          >
            <div className="notifications__btngroup">
              {unseenCount > 0 && (
                <button type="button" onClick={handleMarkSeenAll}>
                  Mark all as read
                </button>
              )}

              {notifications.length > 0 && (
                <button type="button" onClick={handleDeleteAll}>
                  Clear all
                </button>
              )}
            </div>

            {notifications.length === 0 ? (
              <p className="notifications__empty">No notifications</p>
            ) : unseenCount === 0 ? (
              <p className="notifications__empty">No unread notifications</p>
            ) : null}

            {notifications.map((notification) => (
              <PersonNotification
                key={notification.id}
                notification={notification}
                userId={currentUserId}
                handleClose={handleClose}
              />
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

function PersonNotifications({ children }) {
  const [activity, setActivity] = useState(getCallActivity);
  useEffect(() => subscribeCallActivity(setActivity), []);
  const currentUser = useSelector((state) => state.user.currentUser);
  const currentUserId = currentUser?.uid;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackNotification, setSnackNotification] = useState(null);
  const [showIncomingCall, setShowIncomingCall] = useState(false);
  const [callBusy, setCallBusy] = useState(false);
  const [callAudioBlocked, setCallAudioBlocked] = useState(false);

  const [, setTokenFound] = useState(false);

  const notificationAudioRef = useRef(null);
  const callAudioRef = useRef(null);

  const snackTimerRef = useRef(null);
  const knownNotificationIdsRef = useRef(new Set());
  const hasLoadedNotificationsRef = useRef(false);

  const unseenCount = notifications.filter(
    (notification) => !notification.seen,
  ).length;

  const incomingCall =
    !activity &&
    notifications.find((notification) => {
      const createdAt =
        notification.created?.toMillis?.() ||
        (notification.created?.seconds || 0) * 1000;
      return (
        notification.toUserId === currentUserId &&
        notification.callSessionId &&
        getNotificationCallType(notification) &&
        !notification.seen &&
        !notification.silent &&
        notification.expiresAt > Date.now() &&
        createdAt > Date.now() - 60_000 &&
        !currentUser?.blockedUsers?.includes(notification.otherUserId) &&
        !currentUser?.mutedUsers?.includes(notification.otherUserId) &&
        !currentUser?.mutedChats?.includes(
          chatKey(
            notification.callType,
            notification.spaceId,
            notification.stationId,
          ),
        )
      );
    });

  const incomingCallId = incomingCall?.id;

  useEffect(() => {
    if (currentUserId)
      migrateChatMutes().catch((error) =>
        console.error("Chat preferences:", error),
      );
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId || isIOSDevice()) return undefined;

    let cancelled = false;
    let registering = false;
    let lastRegistered = 0;
    const refreshInterval = 24 * 60 * 60 * 1000;

    async function registerNotificationToken() {
      if (
        cancelled ||
        registering ||
        Date.now() - lastRegistered < refreshInterval
      )
        return;
      registering = true;
      try {
        const token = await registerBrowserToken(
          currentUserId,
          async () => {
            const token = await getToken(setTokenFound);
            return cancelled ? null : token;
          },
          getDeviceType(),
        );
        if (token) lastRegistered = Date.now();
      } catch (error) {
        console.error("Could not register notification token:", error);
      } finally {
        registering = false;
      }
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") registerNotificationToken();
    };
    registerNotificationToken();
    const timer = setInterval(refreshWhenVisible, refreshInterval);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) {
      setNotifications([]);
      return undefined;
    }

    let alive = true;
    setNotifications([]);
    setShowSnackBar(false);
    knownNotificationIdsRef.current = new Set();
    hasLoadedNotificationsRef.current = false;

    const unsubscribe = db
      .collection("users")
      .doc(currentUserId)
      .collection("pnotifications")
      .orderBy("created", "desc")
      .limit(9)
      .onSnapshot(
        (querySnapshot) => {
          if (!alive) return;
          const nextNotifications = querySnapshot.docs.map((document) => ({
            ...document.data(),
            id: document.id,
            toUserId: currentUserId,
          }));

          const knownIds = knownNotificationIdsRef.current;

          const newNotification = hasLoadedNotificationsRef.current
            ? nextNotifications.find(
                (notification) =>
                  !notification.seen &&
                  !notification.silent &&
                  !knownIds.has(notification.id),
              )
            : null;

          knownNotificationIdsRef.current = new Set(
            nextNotifications.map((notification) => notification.id),
          );

          hasLoadedNotificationsRef.current = true;

          setNotifications(nextNotifications);

          if (newNotification && !getNotificationCallType(newNotification)) {
            setSnackNotification(newNotification);
            setShowSnackBar(true);

            playAudio(notificationAudioRef.current, true);

            if (snackTimerRef.current) {
              clearTimeout(snackTimerRef.current);
            }

            snackTimerRef.current = setTimeout(() => {
              setShowSnackBar(false);
              snackTimerRef.current = null;
            }, 4000);
          }
        },
        (error) => {
          console.error("Could not load notifications:", error);
        },
      );

    return () => {
      alive = false;
      unsubscribe();
    };
  }, [currentUserId]);

  useEffect(() => {
    setShowIncomingCall(false);
    if (!incomingCall) {
      stopAudio(callAudioRef.current);
      return;
    }
    let valid = false;
    let expired = false;
    const sound = () => {
      if (!valid || expired || document.hidden) {
        stopAudio(callAudioRef.current);
        return;
      }
      callAudioRef.current?.play()?.catch(() => setCallAudioBlocked(true));
    };
    const off = db
      .collection("rooms")
      .doc(incomingCall.roomId || incomingCall.conversationRef)
      .onSnapshot(
        (snapshot) => {
          const room = snapshot.data();
          valid =
            room?.callSessionId === incomingCall.callSessionId &&
            !room.callEndedAt &&
            room.callParticipantIds?.includes(room.callStarterId) &&
            !room.callParticipantIds?.includes(currentUserId);
          setShowIncomingCall(!!valid && !expired);
          sound();
        },
        () => {
          valid = false;
          setShowIncomingCall(false);
          sound();
        },
      );
    const timer = setTimeout(
      () => {
        expired = true;
        setShowIncomingCall(false);
        sound();
      },
      Math.max(0, incomingCall.expiresAt - Date.now()),
    );
    document.addEventListener("visibilitychange", sound);
    return () => {
      off();
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", sound);
      stopAudio(callAudioRef.current);
    };
  }, [
    incomingCallId,
    currentUserId,
    incomingCall?.silent,
    currentUser?.mutedUsers,
    currentUser?.blockedUsers,
    currentUser?.mutedChats,
  ]);

  useEffect(() => {
    return () => {
      if (snackTimerRef.current) {
        clearTimeout(snackTimerRef.current);
      }

      stopAudio(notificationAudioRef.current);
      stopAudio(callAudioRef.current);
    };
  }, []);

  const handleMarkSeenAll = () => {
    if (!currentUserId) return;
    personNotfUpdateSeenAll(currentUserId);
  };

  const handleDeleteAll = () => {
    if (!currentUserId) return;
    personNotfUpdateDeleteAll(currentUserId);
  };

  const respond = async (accept) => {
    if (!incomingCall || callBusy || getCallActivity()) return;
    setCallBusy(true);
    stopAudio(callAudioRef.current);
    try {
      await respondToCall(incomingCall, accept ? "accepted" : "declined");
      if (accept) {
        const chatParams = getCallChatParams(incomingCall);
        if (!chatParams) throw new Error("This call has no chat destination.");
        const next = new URLSearchParams(searchParams);
        ["dmchat", "wschat", "prchat", "fromws"].forEach((key) =>
          next.delete(key),
        );
        Object.entries(chatParams).forEach(([key, value]) =>
          next.set(key, value),
        );
        next.set("acceptCall", incomingCall.callSessionId);
        next.set(
          "acceptRoom",
          incomingCall.roomId || incomingCall.conversationRef,
        );
        setSearchParams(next);
      }
      setShowIncomingCall(false);
    } catch (error) {
      window.alert(error.message || "This call is unavailable.");
    } finally {
      setCallBusy(false);
    }
  };
  const handleAcceptCall = () => respond(true);
  const handleDeclineCall = () => respond(false);

  const handleClose = () => {
    setShowNotifications(false);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unseenCount,
        showNotifications,
        setShowNotifications,
        showSnackBar,
        currentUserId,
        handleMarkSeenAll,
        handleDeleteAll,
        handleClose,
      }}
    >
      {children}

      {/* Keep the existing mobile burger globally for now.
          It can be moved into the top bar separately later. */}
      <div className="app__notifications-container">
        <MobileBurger />
      </div>

      <div className="notification__sliding">
        <AnimatePresence>
          {showSnackBar && snackNotification && (
            <motion.div
              key={snackNotification.id}
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 32, opacity: 1 }}
              exit={{
                x: -100,
                opacity: 0,
                transition: { duration: 1 },
              }}
            >
              <p>New {snackNotification.type || "notification"}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {createPortal(
        <div className="notification__sliding-call">
          <AnimatePresence>
            {showIncomingCall && incomingCall && (
              <motion.div
                key={incomingCall.id}
                initial={{ x: 80, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{
                  x: -100,
                  opacity: 0,
                  transition: { duration: 0.4 },
                }}
              >
                <div
                  className="notification__call"
                  role="dialog"
                  aria-label="Incoming call"
                >
                  <Avatar src={incomingCall.icon} />

                  <h4>{incomingCall.title || "Incoming call"}</h4>

                  {callAudioBlocked && (
                    <button
                      type="button"
                      onClick={() => {
                        callAudioRef.current
                          ?.play()
                          ?.then(() => setCallAudioBlocked(false))
                          .catch(() => {});
                      }}
                    >
                      Enable ringtone
                    </button>
                  )}
                  <div className="notification__call-btns">
                    <button
                      type="button"
                      disabled={callBusy}
                      onClick={handleDeclineCall}
                      className="notf_call-btn-decline"
                    >
                      <BsTelephoneXFill size="1.4em" />
                      Decline
                    </button>

                    <button
                      type="button"
                      disabled={callBusy}
                      onClick={handleAcceptCall}
                      className="notf_call-btn-accept"
                    >
                      <BsFillTelephoneInboundFill size="2em" />
                      Accept
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>,
        document.body,
      )}

      <audio
        ref={notificationAudioRef}
        src="/media/mixkit-guitar-notification-alert-2320.wav"
        preload="auto"
        className="notification__audio"
      />

      <audio
        ref={callAudioRef}
        src="/assets/sounds/mixkit_bell_of_promise_930.wav"
        preload="auto"
        loop
        className="notification__audio"
      />
    </NotificationContext.Provider>
  );
}

export default PersonNotifications;
